import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import { aiProactiveMessages } from '@atlaskit/editor-common/messages';
import TextSpellcheckIcon from '@atlaskit/icon/core/text-spellcheck';
import PresenceActiveIcon from '@atlaskit/icon/glyph/presence-active';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type {
	TransformAction,
	RecommendationDetails,
	RecommendationType,
} from '../../../pm-plugins/ai-proactive/api';
import { getRecommendationCard } from '../../../pm-plugins/ai-proactive/suggestion-setting-map';

const pressableStyles = xcss({
	color: 'color.text.subtlest',
	fontWeight: 'font.weight.lighter',
	padding: 'space.0',
	backgroundColor: 'color.background.neutral.subtle',
});

const pressableSelectedStyles = xcss({
	textDecoration: 'underline',
});

const pressableDisabledStyles = xcss({
	color: 'color.text.disabled',
});

const containerStyles = xcss({
	display: 'flex',
	paddingInline: 'space.150',
	paddingBlock: 'space.150',
	flexDirection: 'column',
	gap: 'space.100',
	alignSelf: 'stretch',
	backgroundColor: 'elevation.surface',
	cursor: 'pointer',
	borderRadius: 'border.radius.100',
});

const extraPadding = xcss({
	paddingBlockStart: 'space.050',
});

const containerHoveredStyles = xcss({
	backgroundColor: 'elevation.surface.hovered',
});

const containerSelectedStyles = xcss({
	backgroundColor: 'color.background.selected',
});

const containerDisabledStyles = xcss({
	color: 'color.text.disabled',
	cursor: 'not-allowed',
});

type ListItemProps = {
	title: string;
	description?: string;
	onClick: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	iconBefore: React.ReactNode;
	iconAfter: React.ReactNode;
	actionButtons?: React.ReactNode[];
	isSelected?: boolean;
	isHovered?: boolean;
	isDisabled?: boolean;
};
const ListItem = (props: ListItemProps) => {
	const {
		title,
		description,
		onClick,
		onMouseEnter,
		onMouseLeave,
		isHovered,
		isSelected,
		isDisabled,
		iconBefore,
		iconAfter,
		actionButtons,
	} = props;

	return (
		<Box
			onClick={!isDisabled ? onClick : undefined}
			onMouseEnter={!isDisabled ? onMouseEnter : undefined}
			onMouseLeave={!isDisabled ? onMouseLeave : undefined}
		>
			<Stack
				xcss={[
					containerStyles,
					!isDisabled && isSelected && containerSelectedStyles,
					!isDisabled && isHovered && !isSelected && containerHoveredStyles,
					isDisabled && containerDisabledStyles,
				]}
			>
				<Inline space="space.150" alignBlock="center">
					{iconBefore}
					<Inline grow="fill">
						<Text
							weight="semibold"
							size="large"
							color={isDisabled ? 'color.text.disabled' : 'color.text'}
						>
							{title}
						</Text>
					</Inline>
					{iconAfter}
				</Inline>
				{!!description && (
					<Text color={isDisabled ? 'color.text.disabled' : 'color.text.subtle'}>
						{description}
					</Text>
				)}
				{!!actionButtons?.length && (
					<Box xcss={extraPadding}>
						<Inline space="space.150" alignBlock="center">
							{actionButtons}
						</Inline>
					</Box>
				)}
			</Stack>
		</Box>
	);
};

type RecommendationItemProps = {
	id: string;
	action?: TransformAction;
	type?: RecommendationType;
	details?: RecommendationDetails;
	isViewed: boolean;
	isHovered: boolean;
	isSelected: boolean;
	isDisabled?: boolean;
	onApply: () => void;
	onDismiss: () => void;
	onClick: () => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onPreview: () => void;
};

export const RecommendationItem = (props: RecommendationItemProps) => {
	const {
		action,
		type,
		details,
		isViewed,
		isHovered,
		isSelected,
		isDisabled,
		onApply,
		onDismiss,
		onClick,
		onMouseEnter,
		onMouseLeave,
		onPreview,
	} = props;
	const { formatMessage } = useIntl();
	const {
		description: descriptionDescriptor,
		title: titleDescriptor,
		icon: Icon,
	} = getRecommendationCard({
		transformAction: action,
		type: type,
		details: details,
	});

	const title = formatMessage(titleDescriptor);
	const description = formatMessage(descriptionDescriptor);

	const previewButton = (
		<Pressable
			key="preview"
			xcss={[
				pressableStyles,
				isHovered && pressableSelectedStyles,
				isDisabled && pressableDisabledStyles,
			]}
			onClick={onPreview}
			isDisabled={isDisabled}
		>
			{formatMessage(aiProactiveMessages.recommendationPreviewButtonLabel)}
		</Pressable>
	);

	const insertButton = (
		<Pressable
			key="replace"
			xcss={[pressableStyles, isDisabled && pressableDisabledStyles]}
			onClick={onApply}
			isDisabled={isDisabled}
		>
			{formatMessage(aiProactiveMessages.recommendationReplaceButtonLabel)}
		</Pressable>
	);

	const dismissButton = (
		<Pressable
			key="dismiss"
			xcss={[pressableStyles, isDisabled && pressableDisabledStyles]}
			onClick={onDismiss}
			isDisabled={isDisabled}
		>
			{formatMessage(aiProactiveMessages.recommendationDismissButtonLabel)}
		</Pressable>
	);

	const actionButtons = isSelected ? [insertButton, dismissButton] : [previewButton, dismissButton];

	return (
		<ListItem
			title={title}
			description={description}
			isHovered={isHovered}
			isSelected={isSelected}
			isDisabled={isDisabled}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			iconBefore={
				<Icon
					size="small"
					label={title}
					color={isDisabled ? token('color.icon.disabled') : token('color.icon')}
				/>
			}
			iconAfter={
				isViewed ? null : (
					// FIXME migrate icon
					// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
					<PresenceActiveIcon
						size="small"
						label="unread"
						primaryColor={
							isDisabled ? token('color.icon.disabled') : token('color.icon.information')
						}
					/>
				)
			}
			actionButtons={actionButtons}
		/>
	);
};

type SpellingGrammarItemProps = {
	errorCount: number;
	onClick: () => void;
};

export const SpellingGrammarItem = (props: SpellingGrammarItemProps) => {
	const { errorCount, onClick } = props;
	const { formatMessage } = useIntl();
	return (
		<ListItem
			title={formatMessage(aiProactiveMessages.spellingAndGrammarLabel)}
			onClick={onClick}
			iconBefore={
				<TextSpellcheckIcon
					label={formatMessage(aiProactiveMessages.spellingAndGrammarLabel)}
					color={token('color.icon')}
				/>
			}
			iconAfter={<Badge>{errorCount}</Badge>}
		/>
	);
};
